import {createTheme} from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';

// ─── Entrio Design Tokens ──────────────────────────────────────────────────────
// Single source of truth for all colors, spacing, radii, and shadows.
// Replace ALL hardcoded hex values in sx props with these tokens.

export const neutral = {
  0: '#FFFFFF',
  25: '#FAFBFC',
  50: '#F5F7F9',
  100: '#EEF1F5',
  200: '#E2E6EC',
  300: '#CDD4DE',
  400: '#9BA5B5',
  500: '#6B7A8D',
  600: '#4A5568',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
} as const;

export const brand = {
  50: '#EEF2FF',
  100: '#DBEAFE',
  500: '#2D68FE',
  600: '#2554D9',
  700: '#1E40AF',
} as const;

export const semantic = {
  success: {main: '#16A34A', light: '#F0FDF4', dark: '#15803D'},
  warning: {main: '#D97706', light: '#FFFBEB', dark: '#B45309'},
  error: {main: '#DC2626', light: '#FEF2F2', dark: '#B91C1C'},
  info: {main: '#2563EB', light: '#EFF6FF', dark: '#1D4ED8'},
} as const;

export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  full: 9999,
} as const;

export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,0.05)',
  md: '0 2px 8px rgba(0,0,0,0.08)',
  lg: '0 4px 16px rgba(0,0,0,0.12)',
} as const;

// ─── Theme ─────────────────────────────────────────────────────────────────────

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brand[500],
      dark: brand[600],
      light: brand[100],
    },
    secondary: {
      main: neutral[700],
    },
    text: {
      primary: neutral[800],
      secondary: neutral[500],
      disabled: neutral[400],
    },
    error: {
      main: semantic.error.main,
      light: semantic.error.light,
      dark: semantic.error.dark,
    },
    success: {
      main: semantic.success.main,
      light: semantic.success.light,
      dark: semantic.success.dark,
    },
    warning: {
      main: semantic.warning.main,
      light: semantic.warning.light,
      dark: semantic.warning.dark,
    },
    info: {
      main: semantic.info.main,
      light: semantic.info.light,
      dark: semantic.info.dark,
    },
    divider: neutral[200],
    background: {
      default: neutral[50],
      paper: neutral[0],
    },
    action: {
      hover: neutral[50],
      selected: brand[50],
      disabled: neutral[300],
      disabledBackground: neutral[100],
    },
  },

  typography: {
    fontFamily: ['Inter', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    h4: {fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.33, letterSpacing: '-0.02em', color: neutral[900]},
    h5: {fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.01em', color: neutral[800]},
    h6: {fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, color: neutral[800]},
    subtitle1: {fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.43, color: neutral[800]},
    subtitle2: {fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.38, color: neutral[600]},
    body1: {fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43, color: neutral[700]},
    body2: {fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.38, color: neutral[500]},
    caption: {fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.27, letterSpacing: '0.02em', color: neutral[400]},
    overline: {
      fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.33,
      letterSpacing: '0.04em', textTransform: 'uppercase', color: neutral[500],
    },
    button: {textTransform: 'none', fontWeight: 500, fontSize: '0.875rem'},
  },

  shape: {borderRadius: radius.md},

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {backgroundColor: neutral[50]},
      },
    },

    MuiButton: {
      defaultProps: {disableElevation: true},
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          height: 36,
          padding: '0 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        sizeSmall: {height: 32, padding: '0 12px', fontSize: '0.8125rem'},
        sizeLarge: {height: 40, padding: '0 20px'},
        contained: {
          '&:hover': {boxShadow: 'none'},
        },
        outlined: {
          borderColor: neutral[300],
          color: neutral[700],
          '&:hover': {
            borderColor: neutral[400],
            backgroundColor: neutral[50],
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          '&:hover': {backgroundColor: neutral[50]},
        },
        sizeSmall: {padding: 4},
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {borderRadius: radius.sm, fontWeight: 600, fontSize: '0.6875rem', height: 22},
        sizeSmall: {height: 20, fontSize: '0.625rem'},
      },
    },

    MuiCard: {
      defaultProps: {elevation: 0},
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${neutral[200]}`,
        },
      },
    },

    MuiPaper: {
      defaultProps: {elevation: 0},
      styleOverrides: {
        root: {borderRadius: radius.lg},
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: shadow.lg,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {boxShadow: shadow.md},
      },
    },

    MuiTextField: {
      defaultProps: {size: 'small'},
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.md,
            fontSize: '0.875rem',
            '& fieldset': {borderColor: neutral[300]},
            '&:hover fieldset': {borderColor: neutral[400]},
            '&.Mui-focused fieldset': {borderColor: brand[500], borderWidth: 2},
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          fontSize: '0.875rem',
          '& fieldset': {borderColor: neutral[300]},
          '&:hover fieldset': {borderColor: neutral[400]},
          '&.Mui-focused fieldset': {borderColor: brand[500], borderWidth: 2},
        },
        input: {
          '&.Mui-disabled': {WebkitTextFillColor: neutral[500]},
        },
      },
    },

    MuiSelect: {
      defaultProps: {size: 'small'},
      styleOverrides: {
        root: {borderRadius: radius.md, fontSize: '0.875rem'},
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {fontSize: '0.875rem'},
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: neutral[25],
            color: neutral[500],
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: `1px solid ${neutral[200]}`,
            padding: '12px 16px',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          color: neutral[700],
          borderBottom: `1px solid ${neutral[100]}`,
          padding: '12px 16px',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {backgroundColor: neutral[50]},
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          border: `1px solid ${neutral[200]}`,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          border: `1px solid ${neutral[200]}`,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          padding: '8px 16px',
          minHeight: 36,
          '&:hover': {backgroundColor: neutral[50]},
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: neutral[500],
          minHeight: 40,
          '&.Mui-selected': {color: brand[500], fontWeight: 600},
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {height: 2, borderRadius: 1},
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {borderRadius: radius.md, fontSize: '0.875rem'},
        standardError: {
          backgroundColor: semantic.error.light,
          border: `1px solid ${semantic.error.main}33`,
          '& .MuiAlert-icon': {color: semantic.error.main},
        },
        standardSuccess: {
          backgroundColor: semantic.success.light,
          border: `1px solid ${semantic.success.main}33`,
          '& .MuiAlert-icon': {color: semantic.success.main},
        },
        standardWarning: {
          backgroundColor: semantic.warning.light,
          border: `1px solid ${semantic.warning.main}33`,
          '& .MuiAlert-icon': {color: semantic.warning.main},
        },
        standardInfo: {
          backgroundColor: semantic.info.light,
          border: `1px solid ${semantic.info.main}33`,
          '& .MuiAlert-icon': {color: semantic.info.main},
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 600,
          padding: '20px 24px 12px',
          color: neutral[900],
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {padding: '12px 24px'},
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {padding: '12px 24px 20px', gap: 8},
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {borderRadius: radius.full, height: 6},
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          fontSize: '0.75rem',
          borderRadius: radius.sm,
          padding: '6px 10px',
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        root: {fontSize: '0.8125rem', color: neutral[500]},
      },
    },
  },
});

export default theme;
